import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import {
  Grid,
  Input,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TablePaginationProps,
} from '@mui/material';

import { TablePagination } from '../../components';
import { Table } from '../../components/table/table';
import { Filter } from '../../components/filter/filter';
import { HeadCell, Row, RowCell } from '../../components/table/types';
import { FilterButton, FilterField } from '../../components/filter/types';

import { UTCToDateTime } from '../../utils/dates';
import { RouterPaths } from '../../router/paths';
import { GetProfilesDTO, useLazyGetProfilesQuery } from '../../api';

export const Profiles: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // api
  const [
    getProfiles,
    { data: profiles, isLoading: isProfilesLoading, originalArgs: originalProfilesArgs },
  ] = useLazyGetProfilesQuery();

  // variables
  const [isSearching, setSearchingStatus] = useState(false);

  // pagination logic
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const handleChangePage: TablePaginationProps['onPageChange'] = (e, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage: TablePaginationProps['onRowsPerPageChange'] = (e) => {
    setPage(0);
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  // filter logic
  const [filterValues, setFilterValues] = useState<Partial<GetProfilesDTO['params']> | null>(null);
  const handleFilterValues = (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<
          | GetProfilesDTO['params']['group']
          | GetProfilesDTO['params']['type']
          | GetProfilesDTO['params']['status']
          | 'all'
        >
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    setFilterValues((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const searchByFilter = async () => {
    if (!originalProfilesArgs || !filterValues) return;

    try {
      await getProfiles({
        ...originalProfilesArgs,
        params: {
          ...originalProfilesArgs.params,
          page: 1,

          ...filterValues,
          // @ts-ignore
          group: filterValues.group === 'all' ? undefined : filterValues.group,
        },
      }).unwrap();
      setSearchingStatus(true);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };
  const resetFilter = async () => {
    if (!originalProfilesArgs || !filterValues) return;

    try {
      filterValues &&
        (await getProfiles({
          ...originalProfilesArgs,
          params: {
            ...originalProfilesArgs.params,
            page: 1,
            group: undefined,
            application_uid: undefined,
            search_pattern: undefined,
          },
        }).unwrap());
      setFilterValues(null);
      setSearchingStatus(false);
      setPage(0);
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  const filterFields: FilterField[] = [
    {
      name: 'search_pattern',
      title: t('profiles.filter.search'),
      component: (
        <Input
          name="search_pattern"
          value={filterValues?.search_pattern ?? ''}
          onChange={(e) => handleFilterValues(e)}
          sx={{ minWidth: '280px', maxWidth: '360px' }}
          fullWidth
        />
      ),
    },
    {
      name: 'reference_number',
      title: t('profiles.filter.reference-number'),
      component: (
        <Input
          name="reference_number"
          value={filterValues?.reference_number ?? ''}
          onChange={(e) => handleFilterValues(e)}
          sx={{ width: '140px' }}
          fullWidth
        />
      ),
    },
    {
      name: 'group',
      title: t('profiles.filter.group'),
      component: (
        <Select
          name="group"
          value={filterValues?.group ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          <MenuItem value="all">{t('common.group-filter.all')}</MenuItem>
          <MenuItem value="own">{t('common.group-filter.own')}</MenuItem>
          <MenuItem value="app">{t('common.group-filter.ext')}</MenuItem>
        </Select>
      ),
    },
    {
      name: 'type',
      title: t('profiles.filter.type'),
      component: (
        <Select
          name="type"
          value={filterValues?.type ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          <MenuItem value="LEGAL">{t('common.profile-type.LEGAL')}</MenuItem>
          <MenuItem value="NATURAL">{t('common.profile-type.NATURAL')}</MenuItem>
        </Select>
      ),
    },
    {
      name: 'status',
      title: t('profiles.filter.status'),
      component: (
        <Select
          name="status"
          value={filterValues?.status ?? ''}
          onChange={(e) => handleFilterValues(e)}
          variant="standard"
          sx={{ width: '140px' }}
        >
          <MenuItem value="NEW">{t('common.profile-status.NEW')}</MenuItem>
          <MenuItem value="VERIFIED">{t('common.profile-status.VERIFIED')}</MenuItem>
          <MenuItem value="ARCHIVED">{t('common.profile-status.ARCHIVED')}</MenuItem>
          <MenuItem value="REJECTED">{t('common.profile-status.REJECTED')}</MenuItem>
          <MenuItem value="BLOCKED">{t('common.profile-status.BLOCKED')}</MenuItem>
        </Select>
      ),
    },
  ];
  const filterButtons: FilterButton[] = [
    {
      name: 'search',
      text: t('ui.filter.search'),
      disabled: filterValues === null,
      onClick: () => searchByFilter(),
    },
    {
      name: 'reset-filter',
      text: t('ui.filter.reset'),
      onClick: () => resetFilter(),
    },
  ];

  // fetch data according pagination and filter
  useEffect(() => {
    const args = isSearching
      ? {
          params: {
            page: page + 1,
            page_size: rowsPerPage,
            ...filterValues,
            // @ts-ignore
            group: filterValues.group === 'all' ? undefined : filterValues.group,
          },
        }
      : { params: { page: page + 1, page_size: rowsPerPage } };

    getProfiles(args);
  }, [page, rowsPerPage]);

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('profiles.head-cells.reference-number'),
    },
    {
      id: '2',
      label: t('profiles.head-cells.name'),
    },
    {
      id: '3',
      label: t('profiles.head-cells.type'),
    },
    {
      id: '4',
      label: t('profiles.head-cells.status'),
    },
    {
      id: '5',
      label: t('profiles.head-cells.created-at'),
    },
  ];

  const rows: Row[] | undefined = profiles?.profiles?.map((it) => {
    const { uid, name, reference_number, type, status, created_at } = it;

    const cells: RowCell[] = [
      {
        name: 'reference_number',
        value: reference_number,
      },
      {
        name: 'name',
        value: name,
      },
      {
        name: 'type',
        value: type,
        formatting: (value: string) => {
          return t(`common.profile-type.${value}`);
        },
      },
      {
        name: 'status',
        value: status,
        formatting: (value: string) => {
          return t(`common.profile-status.${value}`);
        },
      },
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
    ];

    return {
      cells,
      onClick: () => navigate(`${RouterPaths.Profiles}/${uid}`),
      hover: true,
      sx: { cursor: 'pointer' },
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Filter filterFields={filterFields} filterButtons={filterButtons} />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
          <TablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={profiles?.total ?? -1}
            totalPages={profiles?.total_pages ?? 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isLoading={isProfilesLoading}
          />
        </Paper>
      </Grid>
    </>
  );
};
